import isEmpty from 'lodash-es/isEmpty';

import { isNotNil } from '@gn-audio/jss-components';
import {
  AvailabilityState,
  getGroupAccessories,
  getGroupsAttributes,
  getPricesByIds,
  getPricesBySkus,
  getPricingApiConfig,
  Group,
  Price,
  ProductType,
  SFCCPricingSearchResult,
} from '@gn-audio/jss-core';

import { mapToStandardPriceModel } from 'api/pricesApi/priceMapper';
import { customizerAccessoryPromoQuery } from 'api/pricesApi/priceQueryParams';
import { StandardPriceModel, WithPrice } from 'api/pricesApi/pricesModel';
import { logServerError } from 'utils/logger';

export interface AccessoryModel extends Group {
  type?: string;
}

export const getSfccGroupAccessories = async (
  groupId: number,
  marketLocale: string
): Promise<WithPrice<AccessoryModel>[]> => {
  try {
    const { isSFCCPricingOn } = getPricingApiConfig();
    const accessories = await getGroupAccessories(groupId, marketLocale);
    if (!accessories || isEmpty(accessories)) {
      return Promise.resolve([]);
    }
    const skus = accessories.map((accessory) => accessory.sku);
    const accessoryProductIds = accessories.map((accessory) => accessory.productId);

    let sfccPrices = [];

    if (isSFCCPricingOn) {
      const sfccPriceObject = await getPricesBySkus<SFCCPricingSearchResult>(
        skus,
        customizerAccessoryPromoQuery
      );
      sfccPrices = sfccPriceObject?.data ? sfccPriceObject.data : [];
    } else {
      sfccPrices = (await getPricesByIds<Price[]>(accessoryProductIds, ProductType.Product)) ?? [];
    }
    const prices: StandardPriceModel[] = sfccPrices.map(mapToStandardPriceModel);

    const availableItems = prices.filter(
      ({ availabilityState }) =>
        availabilityState !== undefined && availabilityState <= AvailabilityState.BackOrder
    );

    const accessoryGroupIds = accessories.map((accessory) => accessory.groupId).filter(isNotNil);
    const attributes = await getGroupsAttributes(accessoryGroupIds, marketLocale);

    return availableItems
      .map((price) => {
        const product = accessories.find((accessory) => price?.sku === accessory.sku);
        const productAttributes = attributes?.find((attr) => product?.groupId === attr?.groupId);
        const typeAttribute = productAttributes?.attributes?.find(
          (attr) => attr.key === 'Meta_Accessorytype'
        );
        if (!product) return;
        const type = typeAttribute?.valueKey;
        const accessory = { ...product, type };
        return { product: accessory, price };
      })
      .filter(isNotNil);
  } catch (error) {
    logServerError(error);
    return [];
  }
};
